/**
 * PlatformAnalyticsPage — Phase 8 (admin-only)
 * Route: /admin/analytics
 */
import React, { useEffect, useState } from "react";
import { Users, LayoutGrid, Gavel, Building2, Wallet, ShieldAlert, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { analyticsAPI, aiAPI } from "../../lib/aiV8";

export default function PlatformAnalyticsPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [ov, setOv] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [topCats, setTopCats] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, g, cat, cty, cv] = await Promise.all([
          analyticsAPI.overview(),
          analyticsAPI.growth(30),
          analyticsAPI.topCategories(6),
          analyticsAPI.topCities(6),
          analyticsAPI.conversion(30),
        ]);
        setOv(o); setGrowth(g); setTopCats(cat); setTopCities(cty); setConv(cv);
      } catch {
        toast.error(lang === "ar" ? "غير مسموح — للمشرف فقط" : "Admin only");
      } finally { setLoading(false); }
    })();
  }, [lang]);

  const runFraudScan = async () => {
    setScanning(true);
    try {
      const r = await aiAPI.fraudEnhancedScan();
      setScanResult(r);
      toast.success(lang === "ar" ? "اكتمل الفحص" : "Scan complete");
    } catch { toast.error(lang === "ar" ? "فشل الفحص" : "Scan failed"); }
    finally { setScanning(false); }
  };

  if (!user) return <div className="p-10 text-center">Login required.</div>;
  if (loading) return <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>;

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#faf9f5]" dir={lang === "ar" ? "rtl" : "ltr"}
         data-testid="platform-analytics-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold mb-1">
            {lang === "ar" ? "لوحة الإدارة" : "Admin"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-[#0a0a0a]">
            {lang === "ar" ? "التحليلات الشاملة" : "Platform Analytics"}
          </h1>
        </div>

        {ov && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={<Users className="w-4 h-4" />} lbl={lang === "ar" ? "مستخدمين" : "Users"}
                        val={ov.users.total} sub={`+${ov.users.new_30d} 30d`} />
            <MetricCard icon={<LayoutGrid className="w-4 h-4" />} lbl={lang === "ar" ? "الإعلانات" : "Listings"}
                        val={ov.listings.total} sub={`${ov.listings.active} active`} />
            <MetricCard icon={<Building2 className="w-4 h-4" />} lbl={lang === "ar" ? "الشركات" : "Businesses"}
                        val={ov.businesses.total} sub={`${ov.businesses.verified} verified`} />
            <MetricCard icon={<Gavel className="w-4 h-4" />} lbl={lang === "ar" ? "مزادات مباشرة" : "Live Auctions"}
                        val={ov.auctions.live} sub={`${ov.auctions.sold_30d} sold 30d`} />
            <MetricCard icon={<Wallet className="w-4 h-4" />} lbl={lang === "ar" ? "إيرادات 30 يوم" : "Revenue 30d"}
                        val={`${(ov.revenue_30d_sar || 0).toLocaleString()} ر.س`} />
            <MetricCard icon={<TrendingUp className="w-4 h-4" />} lbl={lang === "ar" ? "الاشتراكات النشطة" : "Active Subs"}
                        val={ov.subscriptions_active} />
            <MetricCard icon={<ShieldAlert className="w-4 h-4" />} lbl={lang === "ar" ? "نزاعات مفتوحة" : "Open Disputes"}
                        val={ov.disputes_open} />
            <MetricCard icon={<ShieldAlert className="w-4 h-4" />} lbl={lang === "ar" ? "بلاغات مخاطر" : "Risk Flags"}
                        val={ov.risk_flags_open} tone="red" />
          </div>
        )}

        {/* Growth chart (simple bars) */}
        {growth?.series && (
          <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
              {lang === "ar" ? "النمو اليومي (30 يوم)" : "Daily growth (30 days)"}
            </div>
            <GrowthBars series={growth.series} />
          </div>
        )}

        {/* Top categories + cities */}
        <div className="grid md:grid-cols-2 gap-4">
          <TopList title={lang === "ar" ? "أعلى الماركات" : "Top Makes"} items={topCats} lang={lang} testid="top-cats" />
          <TopList title={lang === "ar" ? "أعلى المدن" : "Top Cities"} items={topCities} lang={lang} testid="top-cities" />
        </div>

        {/* Conversion */}
        {conv && (
          <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
              {lang === "ar" ? "معدل التحويل (30 يوم)" : "Conversion (30 days)"}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-lg font-bold">{conv.views}</div><div className="text-[10px] text-[#8a8a8a]">Views</div></div>
              <div><div className="text-lg font-bold">{conv.contacts}</div><div className="text-[10px] text-[#8a8a8a]">Contacts</div></div>
              <div><div className="text-lg font-bold">{conv.deals}</div><div className="text-[10px] text-[#8a8a8a]">Deals</div></div>
              <div><div className="text-lg font-bold text-emerald-700">{conv.conv_view_to_contact_pct}%</div><div className="text-[10px] text-[#8a8a8a]">V→C</div></div>
              <div><div className="text-lg font-bold text-emerald-700">{conv.conv_contact_to_deal_pct}%</div><div className="text-[10px] text-[#8a8a8a]">C→D</div></div>
            </div>
          </div>
        )}

        {/* Fraud scan */}
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
              {lang === "ar" ? "فحص المخاطر المتقدم" : "Enhanced fraud scan"}
            </div>
            <button type="button" onClick={runFraudScan} disabled={scanning}
                    data-testid="run-fraud-scan"
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white bg-red-600 disabled:opacity-40">
              {scanning ? "…" : (lang === "ar" ? "تشغيل الفحص" : "Run scan")}
            </button>
          </div>
          {scanResult ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-[12px]">
              <Kv k="Duplicates" v={scanResult.duplicate_listing_flags} />
              <Kv k="Price outliers" v={scanResult.suspicious_pricing_flags} />
              <Kv k="Fake accounts" v={scanResult.fake_account_flags} />
              <Kv k="High-risk txns" v={scanResult.high_risk_transaction_flags} />
            </div>
          ) : (
            <div className="text-[12px] text-[#8a8a8a]">
              {lang === "ar" ? "اضغط \"تشغيل الفحص\" لتحليل الإعلانات والمعاملات." : "Click \"Run scan\" to analyze listings & transactions."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, lbl, val, sub, tone }) {
  return (
    <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4">
      <div className="flex items-center gap-2 mb-1 text-[#8a6f2e]">{icon}<div className="text-[10px] uppercase tracking-widest font-bold">{lbl}</div></div>
      <div className={`text-2xl font-bold ${tone === "red" ? "text-red-700" : "text-[#0a0a0a]"}`}>{val}</div>
      {sub && <div className="text-[10px] text-[#8a8a8a] mt-1">{sub}</div>}
    </div>
  );
}

function TopList({ title, items, lang, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">{title}</div>
      {items.length === 0 ? (
        <div className="text-[12px] text-[#8a8a8a]">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={it.key + i} className="flex items-center justify-between text-[13px]">
              <span>{it.key || "—"}</span>
              <span className="font-bold text-[#8a6f2e]">{it.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GrowthBars({ series }) {
  const maxV = Math.max(...series.map((s) => s.users + s.listings + s.businesses), 1);
  return (
    <div className="flex items-end gap-[2px] h-32">
      {series.map((s) => {
        const h = ((s.users + s.listings + s.businesses) / maxV) * 100;
        return (
          <div key={s.date} className="flex-1 bg-[#c9a55c]/70 hover:bg-[#c9a55c] rounded-t"
               title={`${s.date}: users ${s.users}, listings ${s.listings}, biz ${s.businesses}`}
               style={{ height: `${h}%`, minHeight: 2 }} />
        );
      })}
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div className="rounded-xl bg-[#faf9f5] p-2">
      <div className="text-lg font-bold text-[#0a0a0a]">{v}</div>
      <div className="text-[10px] text-[#8a8a8a]">{k}</div>
    </div>
  );
}
